import { useEffect, useMemo, useState } from "react";
import { Save, Search, Image as ImageIcon, RotateCcw, Upload } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { T } from "../../i18n/translations";
import { IMAGES } from "../../data/products";
import { inputCls, btnDark, btnLight, Card, SectionTitle } from "./shared";
import { MediaPicker } from "./MediaPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SECTION_LABEL = { nav: "Navigation", hero: "Accueil — Hero", marquee: "Bandeau défilant", products: "Section produits", manifesto: "Manifeste", ritual: "Le rituel", testimonials: "Avis", contact: "Contact & newsletter", footer: "Pied de page", cartDrawer: "Panier", checkoutPage: "Checkout", account: "Espace client", productPage: "Pages produit", payment: "Paiement", sub: "Abonnement", trust: "Réassurance", checkout: "Checkout (ancien)" };
const IMAGE_LABEL = { heroLifestyle: "Image principale (Hero)", ritualTexture: "Image du manifeste" };

const flatten = (obj, prefix = "", out = {}) => {
  for (const k in obj) {
    const v = obj[k]; const p = prefix ? `${prefix}.${k}` : k;
    if (typeof v === "string") out[p] = v;
    else if (v && typeof v === "object") flatten(v, p, out);
  }
  return out;
};

export const SiteEditor = () => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [section, setSection] = useState("hero");
  const [busy, setBusy] = useState(false);
  const [libFor, setLibFor] = useState(null);
  const base = useMemo(() => ({ fr: flatten(T.fr), en: flatten(T.en) }), []);
  useEffect(() => { axios.get(`${API}/site-content`).then((r) => setData({ fr: {}, en: {}, images: {}, ...r.data })).catch(() => setData({ fr: {}, en: {}, images: {} })); }, []);

  const sections = useMemo(() => [...new Set(Object.keys(base.fr).map((k) => k.split(".")[0]))], [base]);
  const keys = useMemo(() => Object.keys(base.fr).filter((k) => (q ? (k + base.fr[k] + (base.en[k] || "")).toLowerCase().includes(q.toLowerCase()) : k.startsWith(section + "."))), [base, q, section]);
  if (!data) return null;

  const set = (lang, k, v) => setData({ ...data, [lang]: { ...data[lang], [k]: v } });
  const save = async () => {
    setBusy(true);
    try {
      const clean = (o, b) => Object.fromEntries(Object.entries(o).filter(([k, v]) => v && v !== b[k]));
      const body = { fr: clean(data.fr, base.fr), en: clean(data.en, base.en), images: Object.fromEntries(Object.entries(data.images).filter(([, v]) => v)) };
      await axios.put(`${API}/admin/site-content`, body, { withCredentials: true }); setData({ ...data, ...body });
      toast.success("Contenu du site publié — visible immédiatement");
    } catch { toast.error("Erreur d'enregistrement"); } finally { setBusy(false); }
  };
  const upload = async (key, file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const r = await axios.post(`${API}/admin/upload`, fd, { withCredentials: true }); setData({ ...data, images: { ...data.images, [key]: r.data.url } }); toast.success("Image téléversée — cliquez sur Publier"); }
    catch { toast.error("Échec du téléversement"); }
  };
  const changed = Object.keys(data.fr).length + Object.keys(data.en).length + Object.keys(data.images).length;

  return (
    <div className="space-y-6" data-testid="admin-site-editor">
      <Card className="p-5 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]"><Search size={14} className="absolute left-3 top-3 text-stone-400" /><input data-testid="site-editor-search" className={`${inputCls} w-full pl-9`} placeholder="Rechercher un texte du site…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
        <select data-testid="site-editor-section" className={inputCls} value={section} onChange={(e) => { setSection(e.target.value); setQ(""); }}>{sections.map((s) => <option key={s} value={s}>{SECTION_LABEL[s] || s}</option>)}</select>
        <span className="text-[11px] font-mono text-stone-500">{changed} modification(s)</span>
        <button data-testid="site-editor-save" onClick={save} disabled={busy} className={btnDark}><Save size={13} /> Publier sur le site</button>
      </Card>

      {libFor && <MediaPicker onPick={(url) => setData((d) => ({ ...d, images: { ...d.images, [libFor]: url } }))} onClose={() => setLibFor(null)} />}
      {!q && (
        <Card className="p-6" data-testid="site-editor-images">
          <SectionTitle>Images du site</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-5">
            {Object.keys(IMAGES).filter((k) => IMAGE_LABEL[k]).map((k) => (
              <div key={k} className="flex gap-4 items-start">
                <img src={data.images[k] || IMAGES[k]} alt="" className="w-28 h-20 object-cover rounded-xl border border-stone-200" />
                <div className="flex-1 space-y-2">
                  <p className="text-sm text-stone-900 flex items-center gap-2"><ImageIcon size={13} /> {IMAGE_LABEL[k]}</p>
                  <button type="button" data-testid={`site-image-library-${k}`} onClick={() => setLibFor(k)} className={btnLight}><ImageIcon size={12} /> Bibliothèque</button>
                  <label className={`${btnLight} cursor-pointer ml-2`}><Upload size={12} /> Remplacer<input data-testid={`site-image-${k}`} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(k, e.target.files[0])} /></label>
                  {data.images[k] && <button onClick={() => setData({ ...data, images: { ...data.images, [k]: "" } })} className="ml-2 text-[11px] text-stone-500 underline">Rétablir l'originale</button>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="divide-y divide-stone-100">
        {keys.length === 0 && <p className="p-6 text-sm text-stone-500">Aucun texte.</p>}
        {keys.slice(0, 120).map((k) => {
          const rows = Math.min(6, Math.ceil(((data.fr[k] || base.fr[k] || "").length + 40) / 70));
          const mod = (data.fr[k] && data.fr[k] !== base.fr[k]) || (data.en[k] && data.en[k] !== base.en[k]);
          return (
            <div key={k} className="p-4 grid lg:grid-cols-[200px_1fr_1fr] gap-3 items-start" data-testid={`site-key-${k}`}>
              <div className="min-w-0"><p className="font-mono text-[10px] text-stone-500 break-all">{k}</p>{mod && <button data-testid={`site-reset-${k}`} onClick={() => { const fr = { ...data.fr }, en = { ...data.en }; delete fr[k]; delete en[k]; setData({ ...data, fr, en }); }} className="mt-1 text-[10px] text-[#C85A32] inline-flex items-center gap-1"><RotateCcw size={10} /> original</button>}</div>
              {["fr", "en"].map((lang) => <textarea key={lang} data-testid={`site-${lang}-${k}`} rows={rows} className={`${inputCls} w-full text-[13px] ${data[lang][k] && data[lang][k] !== base[lang][k] ? "border-[#C85A32]" : ""}`} value={data[lang][k] ?? base[lang][k] ?? ""} onChange={(e) => set(lang, k, e.target.value)} />)}
            </div>
          );
        })}
        {keys.length > 120 && <p className="p-4 text-xs text-stone-500">Affinez la recherche pour voir les {keys.length - 120} autres textes.</p>}
      </Card>
    </div>
  );
};
